import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import type { BrokerConnectionDto, BrokerLoginDto } from '@apna/shared'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { api, ApiRequestError } from '@/lib/api'
import { cn } from '@/lib/utils'
import { CredentialSection } from './CredentialSection'

/**
 * Edit an existing account.
 *
 * `mode` and `clientCode` are deliberately absent and shown read-only:
 *
 *   mode        locked after creation. A connection whose mode changed would
 *               hold a PAPER history and a LIVE history in one row, and no
 *               reader could separate them.
 *   clientCode  the broker's identity for the account and the unique key —
 *               ONCE THE BROKER HAS ACCEPTED IT. Changing it then is adding a
 *               different account, not editing this one, and it would
 *               silently re-point every order already journalled here.
 *
 *               Before that it is editable, because a code the broker has
 *               never accepted is a typo rather than an identity. Measured
 *               2026-11-02: an account carrying `X47115A` was refused on
 *               every read with "Supplied client not mapped under dealer",
 *               and was permanently unusable with no way out but deleting it
 *               and losing its journal. The server decides from
 *               `lastReadinessOkAt` and sends `canChangeClientCode`; this
 *               screen only renders the answer.
 */

interface Props {
  connection: BrokerConnectionDto | null
  interactiveLogins: BrokerLoginDto[]
  marketDataLogins: BrokerLoginDto[]
  onClose: () => void
  onSaved: () => void
  /** How many connections point at each login — the shared-keys warning. */
  usageByLogin: Record<string, number>
  /** Refetch after a credential is created here. */
  onLoginsChanged: () => void
  onDeleteLogin: (id: string, label: string) => void
}

const field =
  'mt-1.5 h-9 w-full rounded-lg border border-input bg-background px-2.5 text-sm text-foreground outline-none transition-colors focus:border-ring'

const labelCls = 'text-[12px] font-medium text-foreground'

export function EditConnectionDialog({
  connection,
  interactiveLogins,
  marketDataLogins,
  onClose,
  onSaved,
  usageByLogin,
  onLoginsChanged,
  onDeleteLogin,
}: Props) {
  const [label, setLabel] = useState('')
  const [interactiveLoginId, setInteractiveLoginId] = useState('')
  const [marketDataLoginId, setMarketDataLoginId] = useState('')
  const [isMasterFeed, setIsMasterFeed] = useState(false)
  /** The broker's own API host. Per account, not per provider — 3.22. */
  const [baseUrl, setBaseUrl] = useState('')
  const [clientCode, setClientCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Re-seed whenever a different row is opened.
  useEffect(() => {
    if (!connection) return
    setLabel(connection.label)
    setInteractiveLoginId(connection.interactiveLogin?.id ?? '')
    setMarketDataLoginId(connection.marketDataLogin?.id ?? '')
    setIsMasterFeed(connection.isMasterFeed)
    setBaseUrl(connection.baseUrl ?? '')
    setClientCode(connection.clientCode)
    setError(null)
  }, [connection])

  if (!connection) return null

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.patch(`/broker/connections/${connection.id}`, {
        label: label.trim(),
        // null clears the attachment; undefined would leave it unchanged.
        interactiveLoginId: interactiveLoginId || null,
        marketDataLoginId: marketDataLoginId || null,
        isMasterFeed,
        baseUrl: baseUrl.trim(),
        // Sent only when it is editable AND actually changed, so an ordinary
        // rename never carries a field the server would have to refuse.
        ...(connection.canChangeClientCode && clientCode.trim() !== connection.clientCode
          ? { clientCode: clientCode.trim() }
          : {}),
      })
      onSaved()
    } catch (err) {
      const detail = err instanceof ApiRequestError ? err.detail : undefined
      const issues = (detail?.issues as { message: string }[] | undefined)?.map((i) => i.message)
      setError(issues?.join(' ') ?? (err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[860px]">
        <DialogHeader>
          <DialogTitle>Edit account</DialogTitle>
          <DialogDescription>
            {connection.providerKey} · {connection.clientCode} · {connection.mode}
            <span className="ml-1 text-muted-foreground/70">
              {connection.canChangeClientCode
                ? '— mode cannot be changed. The client code can, until the broker accepts it.'
                : '— client code and mode cannot be changed.'}
            </span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className={labelCls}>Holder name</span>
              <input value={label} onChange={(e) => setLabel(e.target.value)} required className={field} />
            </label>
            {connection.canChangeClientCode && (
              <label className="block">
                <span className={labelCls}>Order Client ID</span>
                {/*
                  A PICKER when the login told us what it may act for, and a
                  box only when it told us nothing. Typing this by hand is
                  what produced an account the broker refused on every read,
                  and the codes have been sitting in the login reply all
                  along.

                  `list` rather than a <select>: the options are a HINT, not a
                  validator — a gateway that under-reports must not make a
                  working code unenterable.
                */}
                <input
                  value={clientCode}
                  onChange={(e) => setClientCode(e.target.value)}
                  required
                  className={field}
                  {...(connection.clientCodeOptions.length > 0
                    ? { list: `cc-${connection.id}` }
                    : {})}
                />
                {connection.clientCodeOptions.length > 0 && (
                  <datalist id={`cc-${connection.id}`}>
                    {connection.clientCodeOptions.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                )}
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  {connection.clientCodeOptions.length > 0
                    ? `This login reports it can act for: ${connection.clientCodeOptions.join(', ')}.`
                    : 'The broker has not accepted this account yet, so this can still be corrected.'}
                </span>
              </label>
            )}
            <label className="block">
              <span className={labelCls}>
                Broker API URL <span className="text-destructive">*</span>
              </span>
              <input
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                required
                placeholder="http://tradewiin.wiinance.com:3000"
                spellCheck={false}
                className={cn(field, 'num')}
                data-testid="edit-base-url"
              />
            </label>
          </div>

          {/*
            Credentials live HERE now, not in a tab of their own. Nobody
            thinks "I will manage a credential" — they think "I will connect
            this account". `[B1]`: XTS needs both pairs, and a dealer login
            legitimately backs several accounts, which the section says.
          */}
          <CredentialSection
            role="INTERACTIVE"
            providerKey={connection.providerKey}
            logins={interactiveLogins}
            value={interactiveLoginId}
            onChange={setInteractiveLoginId}
            usageByLogin={usageByLogin}
            onCreated={onLoginsChanged}
            onDelete={onDeleteLogin}
          />
          <CredentialSection
            role="MARKET_DATA"
            providerKey={connection.providerKey}
            logins={marketDataLogins}
            value={marketDataLoginId}
            onChange={setMarketDataLoginId}
            usageByLogin={usageByLogin}
            onCreated={onLoginsChanged}
            onDelete={onDeleteLogin}
          />

          {/*
            ROADMAP 3.21. One feed per trader, and it needs its own
            market-data key — there is no platform feed to fall back on.
            The checkbox is hidden rather than disabled without credentials,
            because a checkbox you cannot tick and cannot explain is worse
            than one that is not there; the line below says what to do.
          */}
          {marketDataLoginId ? (
            <label
              className="flex items-start gap-2 text-[12px] text-foreground"
              data-testid="master-feed-toggle"
            >
              <input
                type="checkbox"
                className="mt-0.5"
                checked={isMasterFeed}
                onChange={(e) => setIsMasterFeed(e.target.checked)}
              />
              <span>
                Use this account for live prices
                <span className="mt-0.5 block text-[11px] text-muted-foreground">
                  One account carries the feed. Turning this on moves it here from whichever
                  account has it now — your broker allows one streaming session per market-data
                  key, so it cannot be two.
                </span>
              </span>
            </label>
          ) : (
            <p className="text-[11px] text-muted-foreground" data-testid="master-feed-needs-creds">
              Add market-data credentials above to use this account for live prices.
            </p>
          )}

          {error && (
            <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 items-center rounded-lg border px-4 text-sm font-medium transition-colors hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-5 text-sm font-medium text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Save changes
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
